import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ChatbotService } from '../chatbot.service';

@Component({
    selector: 'app-chatbot-chat-input',
    templateUrl: './chatbot-chat-input.component.html',
    styleUrls: ['./chatbot-chat-input.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class ChatbotChatInputComponent {
  /**
   * Input properties
   */
  readonly message = input.required<FormControl<string>>();
  readonly canSendMessage = input.required<boolean>();
  readonly limitReached = input<boolean>(false);
  readonly remainingMessages = input<number>(20);
  /**
   * Output events
   */
  readonly enterPressed = output<Event>();
  readonly submitBtnClicked = output();
  protected readonly CHATBOT_CHAR_LIMIT = 200;
  chatbotCharCount = signal<number>(0);

  /**
   * Voice dictation state
   */
  private readonly chatbotService = inject(ChatbotService);
  readonly isRecording = signal<boolean>(false);
  readonly isTranscribing = signal<boolean>(false);
  readonly audioLevels = signal<number[]>([]);
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private animationFrameId: number | null = null;
  private static readonly WAVE_BARS = 24;
  // Minimum normalized bar height (raised baseline) and scroll cadence.
  private static readonly WAVE_BASELINE = 0.2;
  private static readonly WAVE_PUSH_INTERVAL_MS = 80;

  get messageCounterColor(): string {
    const remaining = this.remainingMessages();
    if(remaining <= 0) return '#e74c3c';
    if(remaining <= 3) return '#e67e22';
    return '#2ecc71';
  }

  onMessageInput() {
    const currentLength = this.message().value.length;
    this.chatbotCharCount.set(currentLength);
  }

  onsubmitBtnClicked() {
    this.chatbotCharCount.set(0);
    this.submitBtnClicked.emit();
    this.message().reset('');
  }
  
  onEnterPressed(event: Event) {
    event.preventDefault();
    if(this.canSendMessage()) {
      this.onsubmitBtnClicked();
    }
  }

  async toggleRecording() {
    if(this.isTranscribing()) {
      return;
    }
    if(this.isRecording()) {
      this.stopRecording();
    } else {
      await this.startRecording();
    }
  }

  private async startRecording() {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return;
    }
    const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
    this.mediaRecorder = mimeType ? new MediaRecorder(this.mediaStream, { mimeType }) : new MediaRecorder(this.mediaStream);
    this.audioChunks = [];
    this.mediaRecorder.ondataavailable = (event) => {
      if(event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    };
    this.mediaRecorder.onstop = () => this.handleRecordingStopped();
    this.mediaRecorder.start();
    this.isRecording.set(true);
    this.startVisualizer(this.mediaStream);
  }

  private stopRecording() {
    this.isRecording.set(false);
    this.stopVisualizer();
    this.mediaRecorder?.stop();
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
  }

  private startVisualizer(stream: MediaStream) {
    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    source.connect(this.analyser);

    const buffer = new Uint8Array(this.analyser.frequencyBinCount);
    const barsCount = ChatbotChatInputComponent.WAVE_BARS;

    // Start from a full row of baseline-height bars so the waveform scrolls in from the right.
    this.audioLevels.set(new Array(barsCount).fill(ChatbotChatInputComponent.WAVE_BASELINE));
    let lastPush = 0;

    const render = (now: number) => {
      if(!this.analyser) {
        return;
      }
      if(now - lastPush >= ChatbotChatInputComponent.WAVE_PUSH_INTERVAL_MS) {
        lastPush = now;
        this.analyser.getByteTimeDomainData(buffer);
        // Peak deviation from the 128 midpoint over the whole frame = current loudness.
        let peak = 0;
        for(let i = 0; i < buffer.length; i++) {
          const amplitude = Math.abs(buffer[i] - 128) / 128;
          if(amplitude > peak) {
            peak = amplitude;
          }
        }
        const level = Math.max(ChatbotChatInputComponent.WAVE_BASELINE, Math.min(1, peak * 2));
        const next = [...this.audioLevels(), level];
        if(next.length > barsCount) {
          next.shift();
        }
        this.audioLevels.set(next);
      }
      this.animationFrameId = requestAnimationFrame(render);
    };
    this.animationFrameId = requestAnimationFrame(render);
  }

  private stopVisualizer() {
    if(this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.analyser = null;
    this.audioContext?.close();
    this.audioContext = null;
    this.audioLevels.set([]);
  }

  private async handleRecordingStopped() {
    const type = this.mediaRecorder?.mimeType || 'audio/webm';
    const audioBlob = new Blob(this.audioChunks, { type });
    this.audioChunks = [];
    if(audioBlob.size === 0) {
      return;
    }
    this.isTranscribing.set(true);
    try {
      const transcript = await this.chatbotService.transcribe(audioBlob);
      if(transcript?.trim()) {
        const control = this.message();
        const existing = control.value?.trim();
        control.setValue(existing ? `${existing} ${transcript}` : transcript);
        this.chatbotCharCount.set(control.value.length);
      }
    } catch {
      // Dictation failed silently; the user can retry or type manually.
    } finally {
      this.isTranscribing.set(false);
    }
  }
}